import { useState } from 'react';

import { AlertTriangle, ArrowDown, ArrowUp, RotateCcw, Settings2 } from 'lucide-react';

import { cn } from '@/nucleo/utilitarios';
import { ResponsiveModal } from '@/ui/ResponsiveModal';
import { Button } from '@/ui/base/button';
import { Checkbox } from '@/ui/base/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/ui/base/tooltip';
import { aplicarOrdemEstrutural, type TableViewColumn } from '@/ui/table-view/tabelaVisoes';
import type { PreferenciasDaTabela } from '@/ui/table-view/useTableViewPreferences';

interface TableViewControlsProps<T> {
  tableView: PreferenciasDaTabela<T>;
  className?: string;
}

/**
 * Personalizacao de colunas de uma listagem: um botao discreto e um modal.
 *
 * NAO SE USA DIRETO NA TELA. Quem renderiza este botao e o `DataTable`, na
 * ponta direita do cabecalho, quando recebe a prop `personalizacao`. Assim o
 * recurso tem um lugar e um comportamento so em todo o sistema
 * (`padraoUnico.spec.ts` falha se uma tela importar este componente).
 *
 * REORDENAR E POR BOTAO, NAO POR ARRASTAR. Arrastar e mais bonito e inalcancavel
 * por teclado sem um punhado de codigo de `aria-grabbed` que quase nenhum leitor de
 * tela implementa direito. Seta para cima e seta para baixo sao dois botoes com
 * rotulo, funcionam com Tab e Enter e nao exigem nada de ninguem
 * (CLAUDE.md, "Regras de interface", item 5).
 *
 * COLUNA OCULTA QUE EXPLICA UM FILTRO ATIVO GANHA AVISO NO TOPO DO MODAL — e,
 * fora dele, um ponto de alerta no icone (o texto nao cabe na celula do
 * cabecalho; vai para o tooltip e para o nome acessivel do botao). E o caso
 * mais confuso da tela: o usuario filtra por DRE, escondeu a coluna DRE, e passa a
 * ver uma lista recortada sem enxergar o critério — a conclusao natural e que o
 * sistema perdeu registros.
 *
 * SÓ A PARTE PESSOAL, DE PROPÓSITO. O protótipo tem uma versão bem maior deste
 * recurso — 3 abas (Visualização/Colunas/Auditoria), visão salva com escopo
 * compartilhado ou oficial, e trilha de auditoria — sustentada por três tabelas
 * (`table_view_preferences`, `table_views`, `table_view_audit_log`) que não
 * existem neste projeto. Decisão confirmada com o dono do produto em 2026-08-21:
 * fica só mostrar/ocultar/reordenar coluna, em `localStorage` (ver o comentário
 * de `useTableViewPreferences.ts`). Views compartilhadas/oficiais e auditoria
 * ficam registradas como pendência de banco, não se inventa aqui.
 */
export function TableViewControls<T>({ tableView, className }: TableViewControlsProps<T>) {
  const [aberto, setAberto] = useState(false);

  const {
    columns,
    columnOrder,
    visibleColumnIds,
    hiddenFilteredColumns,
    personalizada,
    updateVisibleColumns,
    updateColumnOrder,
    resetColumns,
  } = tableView;

  const porId = new Map(columns.map((coluna) => [coluna.id, coluna]));
  const naOrdem = columnOrder
    .map((id) => porId.get(id))
    .filter((coluna): coluna is TableViewColumn<T> => Boolean(coluna));

  const visiveis = new Set(visibleColumnIds);
  const quantasVisiveis = naOrdem.filter((coluna) => visiveis.has(coluna.id)).length;

  const alternarVisibilidade = (coluna: TableViewColumn<T>) => {
    if (coluna.canHide === false || coluna.isCritical) {
      return;
    }
    const proximas = visiveis.has(coluna.id)
      ? visibleColumnIds.filter((id) => id !== coluna.id)
      : [...visibleColumnIds, coluna.id];
    updateVisibleColumns(proximas);
  };

  /**
   * Troca a coluna com o vizinho reordenavel mais proximo — e nao com o vizinho
   * imediato. Sem isso, uma coluna presa no caminho engoliria o clique: a seta
   * pareceria quebrada em vez de pular o obstaculo.
   *
   * `aplicarOrdemEstrutural` no fim porque a ordem gravada e apenas um pedido; as
   * pontas fixas (selecao, situacao, sincronizacao, acoes) continuam mandando.
   */
  const mover = (id: string, direcao: -1 | 1) => {
    const atual = naOrdem.findIndex((coluna) => coluna.id === id);
    if (atual < 0) {
      return;
    }

    let alvo = atual + direcao;
    while (alvo >= 0 && alvo < naOrdem.length && naOrdem[alvo]?.canReorder === false) {
      alvo += direcao;
    }
    if (alvo < 0 || alvo >= naOrdem.length) {
      return;
    }

    const proxima = [...naOrdem];
    const [movida] = proxima.splice(atual, 1);
    if (movida) {
      proxima.splice(alvo, 0, movida);
    }
    updateColumnOrder(aplicarOrdemEstrutural(proxima).map((coluna) => coluna.id));
  };

  const reordenaveis = naOrdem.filter((coluna) => coluna.canReorder !== false);
  const primeiraReordenavel = reordenaveis[0]?.id;
  const ultimaReordenavel = reordenaveis[reordenaveis.length - 1]?.id;

  const avisoDeFiltro =
    hiddenFilteredColumns.length === 0
      ? null
      : hiddenFilteredColumns.length === 1
        ? 'Um filtro ativo usa uma coluna oculta'
        : `${hiddenFilteredColumns.length} filtros ativos usam colunas ocultas`;

  return (
    <>
      <TooltipProvider delayDuration={150}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              // 28 px à vista, para caber no cabeçalho sem empurrar o rótulo; o
              // `after:` estende a área de toque a 44 px (docs/DESIGN-SYSTEM.md §7,
              // "Alvo de toque") sem mudar o desenho.
              className={cn(
                "text-muted-foreground hover:text-foreground relative h-7 w-7 after:absolute after:-inset-2 after:content-['']",
                className,
              )}
              onClick={() => setAberto(true)}
              aria-label={
                avisoDeFiltro ? `Personalizar colunas — ${avisoDeFiltro.toLowerCase()}` : 'Personalizar colunas'
              }
            >
              <Settings2 className="h-4 w-4" aria-hidden="true" />
              {avisoDeFiltro && (
                <span
                  className="bg-warning ring-background absolute right-0.5 top-0.5 h-2 w-2 rounded-full ring-2"
                  aria-hidden="true"
                />
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left" className="max-w-[280px] p-3">
            <p className="text-foreground text-sm font-semibold">Personalizar colunas</p>
            <p className="text-muted-foreground text-xs leading-relaxed">
              Escolha o que aparece e em que ordem. A escolha vale só para você, neste
              navegador.
            </p>
            {avisoDeFiltro && (
              <p className="text-foreground mt-2 flex items-center gap-1 text-xs font-medium">
                <AlertTriangle className="text-warning h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                {avisoDeFiltro}
              </p>
            )}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>

      <ResponsiveModal
        open={aberto}
        onOpenChange={setAberto}
        title={
          <>
            <Settings2 className="text-primary h-5 w-5" aria-hidden="true" />
            Colunas da tabela
          </>
        }
        description={`${quantasVisiveis} de ${naOrdem.length} colunas visíveis. A escolha fica guardada neste navegador.`}
        desktopMaxWidth="max-w-lg"
        footer={
          <>
            {personalizada && (
              <Button type="button" variant="outline" onClick={resetColumns}>
                <RotateCcw className="mr-2 h-4 w-4" aria-hidden="true" />
                Restaurar padrão
              </Button>
            )}
            <Button type="button" onClick={() => setAberto(false)}>
              Concluir
            </Button>
          </>
        }
      >
        {hiddenFilteredColumns.length > 0 && (
          <div className="border-warning/40 bg-warning/10 mb-4 rounded-md border p-3">
            <p className="text-foreground flex items-center gap-2 text-sm font-medium">
              <AlertTriangle className="text-warning h-4 w-4 shrink-0" aria-hidden="true" />
              A lista está filtrada por algo que você não está vendo
            </p>
            <p className="text-muted-foreground mt-1 text-xs">
              {hiddenFilteredColumns.map((coluna) => coluna.header).join(', ')} —{' '}
              {hiddenFilteredColumns.length === 1 ? 'esta coluna está' : 'estas colunas estão'}{' '}
              oculta
              {hiddenFilteredColumns.length === 1 ? '' : 's'} e um filtro em vigor depende dela
              {hiddenFilteredColumns.length === 1 ? '' : 's'}. Mostre de volta para entender o
              recorte.
            </p>
          </div>
        )}

        <ul className="divide-border divide-y">
          {naOrdem.map((coluna) => {
            const visivel = visiveis.has(coluna.id);
            const presa = coluna.canHide === false || coluna.isCritical === true;
            const fixa = coluna.canReorder === false;
            const idDaCaixa = `coluna-${coluna.id}`;

            return (
              <li key={coluna.id} className="flex items-center gap-3 py-2">
                <Checkbox
                  id={idDaCaixa}
                  checked={visivel}
                  disabled={presa}
                  onCheckedChange={() => alternarVisibilidade(coluna)}
                />
                <label
                  htmlFor={idDaCaixa}
                  className={cn(
                    'flex-1 truncate text-sm',
                    presa ? 'text-muted-foreground' : 'cursor-pointer',
                  )}
                >
                  {coluna.header || rotuloDeColunaSemTitulo(coluna)}
                  {presa && (
                    <span className="text-muted-foreground ml-2 text-xs">(sempre visível)</span>
                  )}
                </label>

                <div className="flex shrink-0 items-center gap-0.5">
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    disabled={fixa || coluna.id === primeiraReordenavel}
                    onClick={() => mover(coluna.id, -1)}
                    aria-label={`Mover ${coluna.header || 'coluna'} para cima`}
                  >
                    <ArrowUp className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    disabled={fixa || coluna.id === ultimaReordenavel}
                    onClick={() => mover(coluna.id, 1)}
                    aria-label={`Mover ${coluna.header || 'coluna'} para baixo`}
                  >
                    <ArrowDown className="h-3.5 w-3.5" aria-hidden="true" />
                  </Button>
                </div>
              </li>
            );
          })}
        </ul>
      </ResponsiveModal>
    </>
  );
}

/**
 * A coluna de selecao em lote tem cabecalho vazio na tabela — o titulo dela e a
 * propria caixa. No modal isso viraria uma linha sem rotulo, impossivel de entender
 * e impossivel de anunciar.
 */
function rotuloDeColunaSemTitulo<T>(coluna: TableViewColumn<T>): string {
  if (coluna.isSelectionColumn) {
    return 'Seleção';
  }
  if (coluna.isActionColumn) {
    return 'Ações';
  }
  return coluna.id;
}
